<template>
      <div
        class="content-container"
        ref="contentContainer"
        @scroll="handleScroll"
      >
        <NavBar @close="closeModal" />
        <div class="content-wrapper">
          <MiniaturesSection
            :prestations="prestations"
            @navigate="handleNavigation"
          />
          <div class="separator"></div>
          <Gallery
            :prestations="prestations"
            @openFullScreen="handleOpenFullScreen"
            ref="gallery"
          />
          <FullScreenOverlay
            :isFullScreen="isFullScreen"
            :image="fullScreenImage"
            :totalImages="totalImages"
            :currentIndex="currentImageIndex"
            :currentSectionName="currentSection"
            :allPrestationsImages="allPrestationsImages"
            @toggleFullScreen="toggleFullScreen"
            @updateIndex="updateIndex"
          />
          <button
            v-if="showScrollButton"
            @click="scrollToTop"
            id="btn-top"
            class="scroll-to-top-button bg-blue-600"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5 15L12 8L19 15"
                stroke="white"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
            </svg>
          </button>
        </div>
      </div>
  </template>
  
  <script>
  import NavBar from "./NavBar.vue";
  import MiniaturesSection from "./MiniaturesSection.vue";
  import Gallery from "./Gallery.vue";
  import FullScreenOverlay from "./FullScreenOverlay.vue";
  
  export default {
    components: {
      NavBar,
      MiniaturesSection,
      Gallery,
      FullScreenOverlay,
    },
    props: {
      prestations: Array,
    },
    data() {
      return {
        currentImageIndex: 0,
        fullScreenImage: null,
        currentSection: null,
        totalImages: 0,
        isFullScreen: false,
        showScrollButton: false,
        allPrestationsImages: []
      };
    },
    methods: {
      closeModal() {
        this.$emit("close");
      },
      handleNavigation(accomodationName) {
        const sectionElement = this.$refs.gallery.$refs[accomodationName][0];
        if (sectionElement) {
          // Apply a temporary margin to ensure visibility because of the sticky navbar
          sectionElement.style.scrollMarginTop = `90px`;
          sectionElement.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
          console.warn(`Element not found for ${accomodationName}`);
        }
      },
  
      handleOpenFullScreen({ image, prestationName }) {
        // Find the selected prestation
        const prestation = this.findPrestationByName(prestationName);
        if (prestation) {
          const allImages = this.aggregateAllImages(); // Aggregate all images from all prestations
          this.allPrestationsImages = allImages
          const currentIndex = this.findImageIndex(allImages, image); // Find the index of the selected image within all images
  
          // Set full screen properties
          this.setFullScreenProperties(
            image,
            currentIndex,
            allImages.length,
            prestationName
          );
        }
      },
  
      findPrestationByName(prestationName) {
        return this.prestations.find(
          (prestation) => prestation.accomodationName === prestationName
        );
      },
  
      aggregateAllImages() {
        return this.prestations.reduce((accumulator, currentElement) => {
          const imagesWithSection = this.mapImagesWithSection(currentElement);
          return accumulator.concat(imagesWithSection);
        }, []);
      },
  
      mapImagesWithSection(prestation) {
        return prestation.picturesAccomodation.map((img) => ({
          ...img,
          section: prestation.accomodationName,
        }));
      },
  
      findImageIndex(allImages, image) {
        return allImages.findIndex((img) => img.imgXL === image.imgXL);
      },
  
      setFullScreenProperties(image, currentIndex, totalImages, prestationName) {
        this.fullScreenImage = image;
        this.currentImageIndex = currentIndex;
        this.totalImages = totalImages;
        this.currentSection = prestationName;
        this.isFullScreen = true; // Set full screen mode to true
      },
  
      updateIndex(newIndex) {
        // Update the current index
        this.currentImageIndex = newIndex;
  
        // Get all images with sections
        const allImagesWithSections = this.getAllImagesWithSections();
  
        // Handle rollover if newIndex is out of bounds
        if (newIndex < 0) {
          this.fullScreenImage = this.getLastImage(allImagesWithSections);
        } else if (newIndex >= allImagesWithSections.length) {
          this.fullScreenImage = this.getFirstImage(allImagesWithSections);
        } else {
          this.fullScreenImage = allImagesWithSections[newIndex];
        }
  
        // Update the current section based on the image
        this.updateCurrentSection();
      },
  
      getAllImagesWithSections() {
        return this.prestations.reduce((accumulator, currentElement) => {
          const imagesWithSection = currentElement.picturesAccomodation.map(
            (img) => ({
              ...img,
              section: currentElement.accomodationName,
            })
          );
          return accumulator.concat(imagesWithSection);
        }, []);
      },
  
      getLastImage(allImages) {
        return allImages[allImages.length - 1];
      },
  
      getFirstImage(allImages) {
        return allImages[0];
      },
  
      updateCurrentSection() {
        this.currentSection = this.fullScreenImage.section;
      },
  
      toggleFullScreen() {
        this.isFullScreen = !this.isFullScreen;
      },
  
      handleScroll() {
        const scrollThreshold = 300; // Adjust this value based on when you want to show the button
        this.showScrollButton =
          this.$refs.contentContainer.scrollTop > scrollThreshold;
      },
      scrollToTop() {
        this.$refs.contentContainer.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      },
    },
  };
  </script>
  
<style scoped>
  .content-container {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    overflow-y: auto;
    min-height: 100vh;
    box-sizing: border-box;
  }
  
  .content-wrapper {
      max-width: 720px;
      width: 78%;
  }
  
  .scroll-to-top-button {
    right: 50px  !important;
  }
  
  .scroll-to-top-button:hover {
    background-color: var(--blue-dark);
  }
  
  .separator {
    border: solid 0.1px var(--grey-grey-300, #dee2e6);
    width: 100%; /* Ensure separator stretches to the full width of its container */
    align-self: center; /* Centers the separator within the content container */
  }
  
  @media (max-width: 700px) {
    .content-wrapper {
        width: 98%;
    }
  }
  
  /* Media Queries for very small screens */
  @media (max-width: 980px) {
    .content-container {
      padding: 0 15px;
    }
  
    .separator {
      max-width: none; /* Allow the separator to extend fully within the padded container */
    }
  }
  </style>
  